// Column sorting for DataTable: the state, the toggle, and the one comparator every
// sorted table shares. A screen adds sorting by holding this hook and handing `sort`
// and `toggle` to the table — the sorting itself is never restated per screen.
import { useState } from 'react';

// The em-dash a column renders in place of a missing value. It is a placeholder, not
// a value, so it sorts with the blanks rather than under the punctuation.
const PLACEHOLDER = '—';

const isBlank = (v) => v === null || v === undefined || v === '' || v === PLACEHOLDER;

// What a cell is worth when compared (or searched): the text it actually SHOWS, never
// the stored column. `stage` and `payment_terms` are machine codes rendered through the
// Hebrew lookups, so ordering by the stored value would sort the table by something the
// screen never displays — 'net' under 'advance' rather than שוטף under מראש. A column
// whose render returns markup instead of text falls back to the raw value, and an
// explicit `sortValue` overrides both.
export const cellText = (column, row) => {
  if (column.sortValue) return column.sortValue(row);
  if (column.render) {
    const shown = column.render(row);
    if (typeof shown === 'string' || typeof shown === 'number') return shown;
  }
  return row[column.key];
};

// Numbers numerically, everything else by the Hebrew collation: localeCompare('he') is
// what orders א before ב instead of by code point, and `numeric` keeps "קמפיין 2"
// before "קמפיין 10".
const compare = (a, b) => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b), 'he', { numeric: true, sensitivity: 'base' });
};

export default function useTableSort(columns, rows, initial = null) {
  const [sort, setSort] = useState(initial);

  // Clicking the sorted column flips its direction; clicking any other starts that
  // one ascending.
  const toggle = (key) => setSort((s) => (s && s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: 'asc' }));

  const column = sort ? columns.find((c) => c.key === sort.key) : null;
  if (!column) return { sort, toggle, rows };

  const sign = sort.dir === 'desc' ? -1 : 1;
  // Array#sort is stable, so rows that tie on the sorted column keep the order they
  // arrived in — the server's ORDER BY name.
  const sorted = [...rows].sort((ra, rb) => {
    const a = cellText(column, ra);
    const b = cellText(column, rb);
    // Blanks sink to the bottom in BOTH directions. Flipping them to the top on a
    // descending click would fill the first screen with the rows that say nothing.
    if (isBlank(a) || isBlank(b)) {
      if (isBlank(a) && isBlank(b)) return 0;
      return isBlank(a) ? 1 : -1;
    }
    return sign * compare(a, b);
  });
  return { sort, toggle, rows: sorted };
}
